import React, { useEffect, useMemo, useState } from 'react';
import { Search, X, User } from 'lucide-react';
import { COLORS } from '../data/mockData';
import { fetchAdminUsers } from '../services/api';

/**
 * Lightweight member picker for admin actions that target an individual user.
 * Loads the first 100 users via /api/admin/users and filters client-side by
 * email/name/user_id substring.
 */
export default function MemberPicker({ open, onClose, onSelect }) {
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true); setErr('');
    fetchAdminUsers(1)
      .then((d) => setUsers(d.users || d.items || d || []))
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load users'))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    if (!q.trim()) return users.slice(0, 50);
    const needle = q.trim().toLowerCase();
    return users.filter((u) =>
      (u.email || '').toLowerCase().includes(needle)
      || (u.name || '').toLowerCase().includes(needle)
      || (u.user_id || '').toLowerCase().includes(needle)
    ).slice(0, 50);
  }, [q, users]);

  if (!open) return null;
  return (
    <div data-testid="member-picker" onClick={onClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 9100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: '#FFF', borderRadius: 16, padding: 24, width: '100%', maxWidth: 540 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontFamily: 'Outfit, sans-serif', color: COLORS.textPrimary, fontSize: 18 }}>
            Pick a member
          </h3>
          <button type="button" onClick={onClose} data-testid="member-picker-close" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ position: 'relative', marginBottom: 12 }}>
          <Search size={16} color={COLORS.textSecondary} style={{ position: 'absolute', left: 12, top: 11 }} />
          <input data-testid="member-picker-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, or user_id…"
            style={{ width: '100%', padding: '10px 12px 10px 36px', border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14 }} />
        </div>

        {err && <div style={{ color: '#B22222', fontSize: 13, padding: '8px 0' }}>{err}</div>}
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: COLORS.textSecondary, fontSize: 13 }}>Loading…</div>
        ) : (
          <div style={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center', color: COLORS.textSecondary, fontSize: 13 }}>No matches.</div>
            ) : filtered.map((u) => (
              <button key={u.user_id} type="button" data-testid={`member-picker-row-${u.user_id}`}
                onClick={() => onSelect(u)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', borderBottom: `1px solid ${COLORS.border}`, backgroundColor: '#FFF', cursor: 'pointer', textAlign: 'left' }}>
                <div style={{ width: 32, height: 32, borderRadius: 16, backgroundColor: `${COLORS.primary}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <User size={14} color={COLORS.primary} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name || u.email}</div>
                  <div style={{ fontSize: 12, color: COLORS.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                </div>
                {u.role === 'admin' && (
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 9999, backgroundColor: `${COLORS.primary}18`, color: COLORS.primary, fontWeight: 700 }}>ADMIN</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
